import React from 'react';
import { motion } from 'framer-motion';

const ServicesSection = () => {
  const services = [
    {
      title: 'Psychometric Assessments',
      description: 'Science backed, personalised reports for clear decision making.'
    },
    {
      title: 'Leadership Development',
      description: 'Build self-awareness and strategic thinking for tomorrow\'s leaders.'
    },
    {
      title: 'Academic Pathways',
      description: 'Datadriven guidance that aligns strengths with career choices.'
    }
  ];

  return (
    <section className="relative bg-gray-100 pt-16 sm:pt-20 lg:pt-32 pb-0 overflow-hidden">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Title - Top Left */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-left mb-12 sm:mb-16 max-w-7xl mx-auto"
        >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-[#113861] leading-tight">
            <span className="font-source font-light italic">Services</span>
            <span className="font-normal ml-2 sm:ml-3 md:ml-4 font-source font-light">
              we offer
            </span>
          </h2>
        </motion.div>

        <div className="relative min-h-[fit-content] lg:min-h-[fit-content] max-w-7xl mx-auto">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 mb-12 sm:mb-16">
            {/* Left Column - Main Service Description */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-4 sm:space-y-6"
            >
              <h3 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-semibold text-[#113861] font-sans leading-tight">
                Science-Driven Strategies for Lasting Impact
              </h3>
              <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-[#2B2B2B] leading-relaxed font-sans font-regular">
                We harness scientific precision to drive personal and organisational success. 
                By integrating cutting-edge research with practical applications, we empower 
                educators, organisations, and individuals to make informed decisions, optimise 
                performance, and unlock their full potential.
              </p>
            </motion.div>

            {/* Right Column - Individual Service Items */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6 sm:space-y-8"
            >
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3 sm:space-x-4 group hover:transform hover:translate-x-2 transition-all duration-300"
                >
                  {/* Green Arrow Icon */}
                  <div className="flex-shrink-0 mt-1 sm:mt-2">
                     <img src="/images/arrow-bulltin.png" alt="arrow" className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12" />
                  </div>

                  {/* Service Content */}
                  <div className="flex-1 max-w-md">
                    <h4 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-sans font-bold text-[#113861] mb-1 sm:mb-2 group-hover:text-lime-600 transition-colors duration-200 leading-tight">
                      {service.title}
                    </h4>
                    <p className="text-sm sm:text-base lg:text-lg xl:text-lg text-[#2B2B2B] leading-relaxed font-sans font-regular">
                      {service.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Diagonal Gradient Section - Full Width */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
        className="relative mt-16 sm:mt-20 lg:mt-32"
      >
        {/* Diagonal Gradient Background */}
        <div className="relative w-full h-32 sm:h-36 md:h-40 lg:h-48 overflow-hidden">
          {/* Main Diagonal Gradient */}
          <div 
            className="absolute top-0 left-0 w-full h-full"
            style={{
              background: 'linear-gradient(90deg, #113861 0%, #42FF00 100%)',
              clipPath: 'polygon(0 0%, 100% 99%, 100% 100%, 0 100%)'
            }}
          ></div>
     
          
          {/* Left Text - In dark blue area */}
          <div className="absolute bottom-8 sm:bottom-10 md:bottom-12 left-8 sm:left-16 md:left-32 lg:left-80 z-10">
            <div className="text-white font-medium text-sm sm:text-base lg:text-lg xl:text-xl max-w-3xl mx-auto font-sans leading-tight">
              <div>Rooted in <span className="text-lime-400 font-bold">Science.</span></div>
              <div>Shaping the <span className="text-lime-400 font-bold">Future.</span></div>
            </div>
          </div>
          
        
        </div>
      </motion.div>
    </section>
  );
};

export default ServicesSection;
